<template lang="html">
    <div>
        <div class="d-flex justify-content-center align-items-center mb-1">
            <h2 class="m-0">{{headerLine1}}</h2>
            <change-status-btn class="ml-2" v-model="reservation" :disabled="getIsSavingReservation || accessPermissionFilter(['restricted'])"></change-status-btn>
            <payment-status-btn class="ml-2" v-if="!getIsLoadingCalculations" :reservation="reservation" />
        </div>

        <h3 class="mb-1 light">{{headerLine2}}</h3>
        <h3 class="mb-1 light" v-if="reservation.lastUpdatedBy && reservation.lastUpdatedDate">{{ headerLine3 }}</h3>
        
		<connected-reservations :reservation="reservation" />

		<div v-if="reservation.status === -5 && filteredCancellationReasons.length > 0" class="cancellationReason">

			<span class=" mr-1 font-weight-400">Canceled at {{ formattedCancellationDate }} - </span>

			<div v-if="editMode && reservation.cancelationReasonId !== null && cancelationReasonIdIsNullButIsToOpenDropBox" class="col-sm-3" v-tooltip="cancellationReasonObject && cancellationReasonObject.description ? cancellationReasonObject.description : reservation.cancelationNote">
				<v-select
					v-if="filteredCancellationReasons.length > 0"
					id="cancellationReason" 
					class="title-area__dropdown darker radius-normal"
					v-model="cancellationReasonObject"
					:options="filteredCancellationReasons"
					:reduce="(reason) => reason.id"
					label="description" placeholder="Select a reason"
					@input="updateCancellationReason(cancellationReasonObject)"
				></v-select>
			</div>

			<div v-if="editMode && !cancelationReasonIdIsNullButIsToOpenDropBox" class="col-sm-3" v-tooltip="cancellationReasonObject && cancellationReasonObject.description ? cancellationReasonObject.description : reservation.cancelationNote">
				<v-select
					v-if="filteredCancellationReasons.length > 0"
					id="cancellationReason" 
					class="title-area__dropdown darker radius-normal"
					v-model="cancellationReasonObject"
					:options="filteredCancellationReasons"
					:reduce="(reason) => reason.id"
					label="description" placeholder="Select a reason"
					@input="updateCancellationReason(cancellationReasonObject)"
				></v-select>
			</div>

			<span v-if="!editMode" class="lightest radius-normal py-1" >
				<div v-if="userClickedOptionOther">
					<input ref="cancelationNote" class="compactInput" type="text" v-model="reservation.cancelationNote" autofocus>
				</div>
				<span v-else>
					<span v-if="cancellationReasonObject && cancellationReasonObject.id">{{ cancellationReasonObject.description }}</span>
					<span v-else>
						<span v-if="!!reservation.cancelationNote">{{ reservation.cancelationNote }}</span>
						<span v-else>Reason not specified</span>
					</span>
				</span>
			</span>

			<span v-if="editMode && cancellationReasonObject && cancellationReasonObject.id === null">
				<input ref="cancelationNote" v-if="cancelationReasonIdIsNullButIsToOpenDropBox" class="compactInput" type="text" v-model="reservation.cancelationNote" autofocus>
				<span v-else>{{ reservation.cancelationNote }}</span>
			</span>

			<span v-if="!editMode && !userClickedOptionOther" @click.stop="enterEditMode" class="pointer"><i class="mr-0 ml-2 ico ico-edit"></i></span>
			<span v-if="editMode && cancellationReasonObject && cancellationReasonObject.id === null && !cancelationReasonIdIsNullButIsToOpenDropBox" @click.stop="editCancelationNoteDirectly" class="pointer"><i class="mr-0 ml-2 ico ico-edit"></i></span>
			<span v-if="!editMode && userClickedOptionOther || editMode && cancellationReasonObject && cancellationReasonObject.id === null" @click.stop="saveEditOtherReason" class="pointer"><i class="fas ml-2 mr-0 fa-save"></i></span>
			<span v-if="editMode || userClickedOptionOther" @click.stop="cancelEdit" class="pointer"><i class="mr-0 ml-2 ico ico-close"></i></span>
		</div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import changeStatusBtn from '@/tools/reservation/changeStatusBtn'
import PaymentStatusBtn from './PaymentStatusBtn.vue'
import ConnectedReservations from './ConnectedReservations'
import extendedVSelect from '@/extends/vue-select'

export default {
	data() {
        return {
            optionsHolderOpen: false,
			cancellationReasonObject: null,
			clonedCancellationReasonObject: null,
			clonedCancelationNote: null,
			editMode: false,
			userClickedOptionOther: false,
			cancelationReasonIdIsNullButIsToOpenDropBox: false,
			defaultOtherReasonObject: {
				description: 'Other',
				reasonType: -9999, // -9999 type doesn't exist
				id: null
			},
			deletedCancellationReasonObject: null
        }
    },
	async mounted() {
		this.userClickedOptionOther = false;
		const cancelationReasonId = this.reservation.cancelationReasonId || null;

		this.bootUpCancellationReasons();

		// If cancellationReasonId is null and cancelationNote is null, then set the cancellationReasonObject to Other reason
		if (!cancelationReasonId && !this.reservation.cancelationNote) {
			this.cancellationReasonObject = this.defaultOtherReasonObject;
			this.cancellationReasonObject.cancelationNote = null;
		}

		// Copy of initial state of cancellationReasonObject
		this.clonedCancellationReasonObject = JSON.parse(JSON.stringify(this.cancellationReasonObject));
		this.clonedCancelationNote = JSON.parse(JSON.stringify(this.reservation.cancelationNote));

		this.eventHub.$on('changesSaved',()=>{
			this.$nextTick(() => {
				this.bootUpCancellationReasons();
			})
		})
	},
	destroyed() {
		this.eventHub.$off('changesSaved');
		this.deletedCancellationReasonObject = null;
	},
    props: {
        reservation: {
            type: Object
        }
    },
	watch: {
		'isReadyToSubmit': {
			handler(newVal, oldVal) {
				this.$emit('titleAreaIsReadyToSubmit', this.isReadyToSubmit);
			},
			deep: true,
			immediate: true,
		},
		editMode: {
			handler(newVal, oldVal) {
				if (!!newVal) {
					// When editMode is true, copy of initial state of cancellationReasonObject
					this.clonedCancellationReasonObject = JSON.parse(JSON.stringify(this.cancellationReasonObject));
					this.clonedCancelationNote = JSON.parse(JSON.stringify(this.reservation.cancelationNote));
				}
			}
		},
		cancelationReasonIdIsNullButIsToOpenDropBox: {
			handler(newVal, oldVal) {
				if (!!newVal) {
					this.$nextTick(() => {
						this.$refs.cancelationNote && this.$refs.cancelationNote.focus();
					})
				}
			}
		}
	},
    computed: {
        ...mapGetters([
            'getIsSavingReservation',
            'getIsLoadingCalculations',
			'getReasons',
			'getAllIntegrations'
        ]),
		foundCancellationReasons() {
			return !!this.getReasons && this.getReasons.filter(
				reason => reason.reasonType === 0 || reason.reasonType === 1
			).length > 0;
		},
		trackCancellationReasonIdAndCancelationNote() {
			return `${this.reservation.cancelationReasonId}|${this.reservation.cancelationNote}}`;
		},
        headerLine1() {
            const bookingNumber = this.formatTicketNumber(this.reservation.id);
            
			return `Booking Nº: ${bookingNumber}`;
        },
        headerLine2() {
			const { createdByName, reservationDate, channelId, channelType } = this.reservation;
			const ota = this.getAllIntegrations.find(i => i.channelId === channelId && i.integration === channelType);
			const name = ota ? ota.name : createdByName;
			const date = `${this.formatDateDots(reservationDate)} ${this.formatTime(reservationDate)}`;

			return `Booked by ${name} at ${date}`;
		},
		headerLine3() {
            const { lastUpdatedBy, lastUpdatedDate } = this.reservation;
            const name = lastUpdatedBy;
            const date = `${this.formatDateDots(lastUpdatedDate)} ${this.formatTime(lastUpdatedDate)}`;
            
			return `Last update by ${name} at ${date}`;
        },
		filteredCancellationReasons() {
			const getReasons = this.foundCancellationReasons ? this.getReasons : [];

			// Get only reason.reasonType === 0 (all reasons) and reason.reasonType === 1 (booking reasons)
			let reasonsAllAndBooking = getReasons.filter((reason) => {
				return reason.reasonType === 0 || reason.reasonType === 1;
			})
			// Inject other reason (manually)
			reasonsAllAndBooking.push(this.defaultOtherReasonObject);
			
			return reasonsAllAndBooking;
		},
		formattedCancellationDate() {
			return moment(this.reservation.cancelationDate).format('DD.MM.YYYY HH:mm');
		},
		isReadyToSubmit() {
			if (this.foundCancellationReasons) return !this.userClickedOptionOther;
			
			return true;
		}
    },
    methods: {
        ...mapActions([
            'closeModal',
            'openReservationById'
        ]),
		async bootUpCancellationReasons() {
			const cancelationReasonId = await this.reservation && await this.reservation.cancelationReasonId;

			if (this.foundCancellationReasons) {
				// If cancellationReasonId is set, then set the cancellationReasonObject based on the id
				if (!!cancelationReasonId) {
					this.cancellationReasonObject = await this.getReasons.find(reason => reason.id == cancelationReasonId);
				}

				// If canceled and cancellationReasonId is null and cancelationNote is not null, then set the cancellationReasonObject to Other reason
				if (cancelationReasonId === null && !!this.reservation.cancelationNote) {
					this.cancellationReasonObject = this.filteredCancellationReasons.find(reason => reason.id == cancelationReasonId);
				}
			} else {
				this.cancellationReasonObject = this.defaultOtherReasonObject;
			}

			// If current reason id coming from reservation if deleted (not found in this.getReasons list), then set the cancellationReasonObject with the data coming from reservation
			const foundCancellationReasonObjectInList = this.getReasons.find(reason => reason.id == cancelationReasonId);
			
			if (!foundCancellationReasonObjectInList && !!cancelationReasonId) {
				this.cancellationReasonObject = {
					description: this.reservation.cancelationNote,
					id: this.reservation.cancelationReasonId
				}
			}
		},
		enterEditMode() {
            this.editMode = true;
        },
		editCancelationNoteDirectly() {
			if (!!this.reservation.cancelationNote) {
				this.editMode = true;
			}
			this.cancelationReasonIdIsNullButIsToOpenDropBox = true;
		},
		saveEditOtherReason() {
			const char = 3
			if (!!this.reservation.cancelationNote && this.reservation.cancelationNote.length > char -1) {
				this.userClickedOptionOther = false;
				this.editMode = false;
			} else {
				if (this.foundCancellationReasons) {
					return this.$toasted.error(`You have to write something with at least ${char} characters`);
				}
				this.userClickedOptionOther = false;
				this.editMode = false;
			}

			if (this.clonedCancelationNote !== this.reservation.cancelationNote) {
				this.$set(this.reservation.updateFlags, 'reservation', true);
			}

			this.cancelationReasonIdIsNullButIsToOpenDropBox = false;
		},
		cancelEdit() {
			this.editMode = false;
			this.userClickedOptionOther = false;
			this.cancellationReasonObject = this.clonedCancellationReasonObject;
			this.reservation.cancelationReasonId = this.clonedCancellationReasonObject.id;
			this.reservation.cancelationNote = this.clonedCancelationNote;
			this.cancelationReasonIdIsNullButIsToOpenDropBox = false;
		},
		updateCancellationReason(reason) {
			this.$set(this.reservation.updateFlags, 'reservation', true);

			if (reason.id === null) {
				this.reservation.cancelationNote = null; // Start null. As the user start typing it will have a value
				this.userClickedOptionOther = true;
				this.$nextTick(() => {
					this.$refs.cancelationNote.focus();
				})
			} else {
				this.userClickedOptionOther = false;
			}

			this.reservation.cancelationReasonId = reason.id;
			this.editMode = false;
			this.cancelationReasonIdIsNullButIsToOpenDropBox = false;
		},
        clickShoppinigCartOtherReservation(reservationId) {
            this.closeModal();
            this.openReservationById(reservationId);
        },
    },
    components: {
        changeStatusBtn,
        PaymentStatusBtn,
        ConnectedReservations,
		vSelect: extendedVSelect,
    }
}
</script>

<style lang="scss" scoped>
	.cancellationReason {
		display: flex;
		justify-content: center;
		margin-top: 4px;
		align-items: center;
	}

	.compactInput {
		height: 1.5rem !important;
	}
</style>

<style lang="scss">
	.title-area__dropdown {
		.dropdown-toggle {
			height: 1.5rem !important;

			.vs__actions {
				top: 3px !important;
			}

			.selected-tag, & input[type=search] {
				height: 1.4rem !important;
				margin: 0 !important;
				line-height: 1.4rem !important;
			}
		}

		.open-indicator {
			height: 14px !important;
		}
	}
</style>



// WEBPACK FOOTER //
// TitleArea.vue?a176a71a